/**
 * Функция для создания маршрутов для CRUD моделей.
 *
 * @author Artem Bakulin <dekkyartem@gmail.com>
 */

import { RouteConfig } from 'vue-router';
import { RoleEnum } from '@/store/user';

interface RouteGeneratorConfig {
    modelName: string,
    url?: string,
    routePrefix?: string,
    roles?: RoleEnum[],
    overwriteRouteFunctions?: {
        [index:string]: (params: any) => any,
    },
}

function defineListRoute(params: {path: string; modelName: string; meta: any}) {
    const {path, modelName, meta} = params

    return {
        path,
        name: `${modelName}`,
        meta,
        component: () => import(`../views/${modelName}/List.vue`),
    }
}

function defineCreateRoute(params: {path: string; modelName: string; meta: any}) {
    const {path, modelName, meta} = params

    return {
        path: `${path}/create`,
        name: `${modelName}_create`,
        meta,
        component: () => import(`../views/${modelName}/Create.vue`),
    }
}

function defineEditRoute(params: {path: string; modelName: string; meta: any}) {
    const {path, modelName, meta} = params

    return {
        path: `${path}/:${modelName}Id`,
        name: `${modelName}_edit`,
        meta,
        component: () => import(`../views/${modelName}/Edit.vue`),
    }
}

/**
 * Функция для создания маршрутов для CRUD моделей.
 *
 */
export const crudRoutes = (params: RouteGeneratorConfig): RouteConfig[] => {
    const {
        modelName,
        routePrefix = '',
        roles,
        overwriteRouteFunctions = {},
        url = modelName.toLowerCase().replaceAll('_', '-')
    } = params

    const path = routePrefix ? `/${routePrefix}/${url}` : `/${modelName}`;
    const meta = {
        requiresAuth: true,
        roles: roles || [RoleEnum.ROLE_DOCTOR],
    };

    const listRoute = (overwriteRouteFunctions.list || defineListRoute)({path, modelName, meta})
    const createRoute = (overwriteRouteFunctions.create || defineCreateRoute)({path, modelName, meta})
    const editRoute = (overwriteRouteFunctions.edit || defineEditRoute)({path, modelName, meta})
    
    // by default there's only 1 route created for: list, create, edit pages
    // but overwrite functions can return an array of routes, so we flatten the route list for that case
    return [
        listRoute,
        createRoute,
        editRoute,
    ].flat()
};